import { RouteComponentProps } from '@reach/router';
import React from 'react';
import {
  AppBbWaardecheckAddress,
  AppVboWaardecheckAddress,
} from '~/graphql/types';
import InputGroupDivider from '~/scenes/Apps/components/InputGroupDivider';

import AppDetailsContainer from '../../../AppDetailsContainer';
import { useRecoilState } from 'recoil';
import {
  AppWaardecheckFieldsFragment,
  appWaardecheckState,
  StateId,
} from '../..';
import { mergeDeepRight } from 'ramda';
import { DeepPartial } from 'utility-types';
import AppSectionHeader from '~/scenes/Apps/components/AppSectionHeader';
import BBWaardecheckIntl from '../../intl';
import AppEntryItemHeader from '~/scenes/Apps/components/AppEntryItemHeader';
import { WaardecheckDefaultData } from '../../utils/getDefaultData';
import Description from '../../components/Description';
import IntlInputComponent from '../../components/IntlInputComponent';
import MainPreview from '../../components/MainPreview';
import ImageInput from '../../components/ImageInput';
import { Page } from '../../components/Preview';
import TEST_ID from './index.testid';
import Explanation from '../../components/Explanation';

export type Props = RouteComponentProps & {
  dataTestId?: string;
  defaultValues: WaardecheckDefaultData;
  data: AppWaardecheckFieldsFragment;
  loading: boolean;
};

const Address: React.FC<Props> = ({
  dataTestId: _dataTestId,
  data,
  defaultValues,
  loading,
}) => {
  const original = defaultValues.address;
  const [updated, setUpdatedValue] = useRecoilState(
    appWaardecheckState(StateId.updated),
  );

  const updateValue = (
    value: DeepPartial<AppBbWaardecheckAddress | AppVboWaardecheckAddress>,
  ) => {
    setUpdatedValue(prev => {
      if (!prev) return prev;

      return mergeDeepRight(prev, {
        address: value,
      }) as any as typeof prev;
    });
  };

  if (original == null || updated == null) return null;

  return (
    <AppDetailsContainer
      header="Adres"
      loading={loading}
      dataTestId={TEST_ID.CONTAINER}
    >
      <Explanation>
        Op deze pagina wordt het adres ingevoerd. Personaliseer de
        achtergrondfoto en teksten.
      </Explanation>
      <MainPreview data={updated} page={Page['/']} />
      <AppEntryItemHeader>
        {BBWaardecheckIntl.mainImage.title}
      </AppEntryItemHeader>
      <Description>{BBWaardecheckIntl.mainImage.description}</Description>
      <ImageInput
        initialUrl={data.address.image?.url}
        filename={data.__typename + 'address'}
        s3Key={updated?.address.image?.s3key}
        onChange={image => updateValue({ image })}
        dataTestid={TEST_ID.IMAGE}
      />

      <InputGroupDivider large />
      <AppSectionHeader>{BBWaardecheckIntl.section.text}</AppSectionHeader>
      <Description>{BBWaardecheckIntl.section.textDescription}</Description>
      <IntlInputComponent
        title={BBWaardecheckIntl.heading}
        defaultValue={original.heading}
        value={updated.address.heading}
        onChange={heading => updateValue({ heading })}
        dataTestId={TEST_ID.HEADING}
      />
      <InputGroupDivider />
      <IntlInputComponent
        title={BBWaardecheckIntl.subHeading}
        defaultValue={original.subHeading}
        value={updated.address.subHeading}
        onChange={subHeading => updateValue({ subHeading })}
        dataTestId={TEST_ID.SUB_HEADING}
      />
      <InputGroupDivider />
      <IntlInputComponent
        title="Primary button"
        defaultValue={original.primaryButton}
        value={updated.address.primaryButton}
        onChange={primaryButton => updateValue({ primaryButton })}
        dataTestId={TEST_ID.PRIMARY_BUTTON}
      />
    </AppDetailsContainer>
  );
};

export default Address;
